import { randomUUID } from 'node:crypto';
import type { LooseAppManifest } from '@wix/cli-app-manifest';
import type { HttpClient, HttpError } from '@wix/cli-http-client';
import {
  getLatestProductionVersion,
  promoteAppVersionFromDraftToProduction,
  suggestNextVersions,
} from '@wix/ambassador-devcenter-apps-v1-app-version/http';
import { PromoteStrategy } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import {
  commitOverride,
  createComponentsOverride,
  updateComponentsOverrides,
  updateOverrideWithNoSideEffects,
} from '@wix/ambassador-devcenter-components-overrides-v1-components-override/http';
import type {
  ComponentFieldMask,
  OverrideType,
  LifecycleState,
} from '@wix/ambassador-devcenter-components-overrides-v1-components-override/types';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isHttpError } from '@wix/cli-http-client';
import { fields, variant } from 'variant';
import {
  getLatestProductionVersionResponseSchema,
  promoteAppVersionFromDraftResponseSchema,
  suggestNextVersionsResponseSchema,
  overrideIdResponseSchema,
} from './schemas.js';
import { handleReleaseError, toModifiedComponents } from './utils.js';

const UpdateComponentsOverridesData = variant({
  NotFound: fields<{ cause: HttpError }>(),
  Updated: {},
});

export class DevCenterOverridesClient {
  constructor(private readonly httpClient: HttpClient) {}

  private getAppLatestVersion = async (appId: string): Promise<number> => {
    try {
      const { data } = await this.httpClient.request(
        getLatestProductionVersion({ appId })
      );

      const { appVersion } =
        getLatestProductionVersionResponseSchema.parse(data);

      return appVersion?.version ?? 0;
    } catch (e) {
      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToFetchLatestProductionVersion(),
      });
    }
  };

  createComponentsOverride = async ({
    type,
    appManifest: { appId, components },
    lifecycleState,
    projectId,
  }: {
    type?: OverrideType;
    appManifest: LooseAppManifest;
    lifecycleState?: LifecycleState;
    projectId: string;
  }): Promise<string> => {
    try {
      const appVersion = await this.getAppLatestVersion(appId);

      const { data } = await this.httpClient.request(
        createComponentsOverride({
          componentsOverride: {
            id: randomUUID(),
            type,
            appId,
            appVersion,
            lifecycleState,
            externalId: projectId,
            modifiedComponents: toModifiedComponents(components),
          },
        })
      );

      return overrideIdResponseSchema.parse(data).componentsOverride.id;
    } catch (e) {
      handleReleaseError(e, appId, CliErrorCode.FailedToCreateAppRelease());
    }
  };

  updateComponentsOverrides = async ({
    id,
    type,
    appManifest: { appId, components },
    projectId,
  }: {
    id: string;
    type?: OverrideType;
    appManifest: LooseAppManifest;
    projectId: string;
  }) => {
    try {
      const appVersion = await this.getAppLatestVersion(appId);

      await this.httpClient.request(
        updateComponentsOverrides({
          componentsOverride: {
            id,
            type,
            appId,
            appVersion,
            externalId: projectId,
            modifiedComponents: toModifiedComponents(components),
          },
          fieldMask: ['type', 'appVersion', 'modifiedComponents'],
        })
      );

      return UpdateComponentsOverridesData.Updated();
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 404) {
        return UpdateComponentsOverridesData.NotFound({ cause: e });
      }
      handleReleaseError(e, appId, CliErrorCode.FailedToUpdateAppRelease());
    }
  };

  updateOverrideWithNoSideEffects = async ({
    id,
    appManifest: { appId, components },
    componentFieldMasks,
    projectId,
  }: {
    id: string;
    appManifest: LooseAppManifest;
    componentFieldMasks: ComponentFieldMask[];
    projectId: string;
  }): Promise<string> => {
    try {
      await this.httpClient.request(
        updateOverrideWithNoSideEffects({
          componentsOverride: {
            id,
            appId,
            externalId: projectId,
            modifiedComponents: toModifiedComponents(components),
          },
          componentFieldMasks,
        })
      );

      return id;
    } catch (e) {
      handleReleaseError(
        e,
        appId,
        CliErrorCode.FailedToUpdateOverrideWithNoSideEffects()
      );
    }
  };

  createDraftVersion = async (appId: string, componentsOverrideId: string) => {
    try {
      await this.httpClient.request(
        commitOverride({ componentsOverrideId, onlyEmitEvent: false })
      );

      const { data } = await this.httpClient.request(
        suggestNextVersions({ appId })
      );

      const { nextMajor, nextMinor } =
        suggestNextVersionsResponseSchema.parse(data);

      return { nextMajor, nextMinor };
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToCreateDraftVersion(),
      });
    }
  };

  releaseDraftVersion = async (
    appId: string,
    options: {
      createNewMajor: boolean;
      latestMajor: number;
      notes: string;
    }
  ) => {
    try {
      const { data } = await this.httpClient.request(
        promoteAppVersionFromDraftToProduction({
          appId,
          createNewMajor: options.createNewMajor,
          notes: options.notes,
          promoteStrategy: PromoteStrategy.ENTIRE_APP,
          version: options.latestMajor,
        })
      );

      const { appVersion } =
        promoteAppVersionFromDraftResponseSchema.parse(data);

      return { releasedVersion: appVersion.fullVersion ?? '' };
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToReleaseDraftVersion(),
      });
    }
  };
}
